<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  inboxId: {
    type: [Number, String],
    default: null,
  },
  teamId: {
    type: [Number, String],
    default: null,
  },
  csvFileName: {
    type: String,
    required: true,
  },
  templateVariables: {
    type: Array,
    default: () => [],
  },
  variableMappings: {
    type: Object,
    default: () => ({}),
  },
  scheduledAt: {
    type: String,
    default: null,
  },
  inboxOptions: {
    type: Array,
    required: true,
  },
  teamOptions: {
    type: Array,
    required: true,
  },
  validator: {
    type: Object,
    required: true,
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
  totalSteps: {
    type: Number,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  scheduledAtError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:scheduledAt', 'back', 'submit']);

const { t } = useI18n();

const currentDateTime = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
});

const isSubmitDisabled = computed(() => {
  return props.validator.$invalid || props.isCreating;
});

const handleSubmit = () => {
  props.validator.$touch();
  if (props.validator.$invalid) return;

  emit('submit');
};

const localScheduledAt = computed({
  get: () => props.scheduledAt,
  set: value => emit('update:scheduledAt', value),
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEPS.REVIEW_AND_SCHEDULE') }}
      </h3>
      <div class="flex items-center gap-2">
        <div class="text-xs text-n-slate-10">
          {{
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEP', {
              current: currentStep + 1,
              total: totalSteps,
            })
          }}
        </div>
      </div>
    </div>

    <div class="p-4 border rounded-md mb-4">
      <h4 class="text-sm font-medium mb-2">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.REVIEW.SUMMARY') }}
      </h4>

      <div class="flex flex-col gap-2">
        <div class="flex justify-between text-sm">
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TITLE.LABEL') }}:
          </span>
          <span class="font-medium">{{ title }}</span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.INBOX.LABEL') }}:
          </span>
          <span class="font-medium">{{
            inboxOptions.find(option => option.value === inboxId)?.label
          }}</span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TEAM.LABEL') }}:
          </span>
          <span class="font-medium">{{
            teamOptions.find(option => option.value === teamId)?.label
          }}</span>
        </div>

        <div class="flex justify-between text-sm">
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.AUDIENCE.LABEL') }}:
          </span>
          <span class="font-medium">{{ csvFileName }}</span>
        </div>

        <div
          v-if="templateVariables.length > 0"
          class="flex flex-col gap-1 text-sm"
        >
          <span class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VARIABLE_MAPPING.TITLE') }}:
          </span>
          <div class="ml-2">
            <div
              v-for="variable in templateVariables"
              :key="variable"
              class="flex justify-between"
            >
              <span class="text-n-slate-10">{{ variable }}:</span>
              <span class="font-medium">
                {{ variableMappings[variable] }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Input
      v-model="localScheduledAt"
      :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="
        t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
      "
      :message="scheduledAtError"
      :message-type="scheduledAtError ? 'error' : 'info'"
    />

    <div class="flex justify-between mt-4">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.BACK')"
        @click="emit('back')"
      />
      <div class="flex items-center gap-2">
        <Button
          :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.CREATE')"
          type="button"
          :is-loading="isCreating"
          :disabled="isCreating || isSubmitDisabled"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>
